import React, { useMemo, useState } from "react"
import { Modal, Button, notification, Dropdown, Menu } from "antd"
import { useSelector } from "react-redux"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { Icon } from "components"
import { ALL_CONTRACTS_VALUE } from "hooks"

interface RouteOptionsModalProps {
	onSyncSuccess: () => void
}

const RouteOptionsButton: React.FC<RouteOptionsModalProps> = ({
	onSyncSuccess,
}) => {
	const [showRoute4meModal, setShowRoute4meModal] = useState(false)
	const [route4meWarning, setRoute4meWarning] = useState(false)
	const [syncing, setSyncing] = useState(false)

	// Redux variables
	const rawFilters = useSelector((state: any) => state.filters) || {}
	const { business, contract, date } = useMemo(
		() => ({
			business: rawFilters.business,
			contract: rawFilters.contract,
			date: rawFilters.date,
		}),
		[rawFilters.business, rawFilters.contract, rawFilters.date]
	)

	const isAllContracts = contract === ALL_CONTRACTS_VALUE

	const handleRoute4meSync = async (allowOnTransitSync = false) => {
		if (!business || !contract || isAllContracts || !date) {
			notification.error({
				message: "Sync Failed",
				description: "Business, contract and date are required",
				placement: "bottomRight",
				duration: 5,
			})
			return
		}

		setSyncing(true)

		const data = {
			business: business,
			contract: contract,
			date: new Date(date).toISOString().split("T")[0],
			allowOnTransitSync,
		}

		try {
			const response = await API.post({
				url: Endpoints.uriRoute4meSync(),
				data,
			})

			if (response.ok) {
				// Check if on transit routes error was returned
				if (response.data.results?.routesOnTransit) {
					setRoute4meWarning(true)
					setSyncing(false)
					return
				}

				notification.success({
					message: "Route4me Sync",
					description: Strings.orders.synced,
					placement: "bottomRight",
					duration: 3,
				})

				setShowRoute4meModal(false)
				setRoute4meWarning(false)
				onSyncSuccess()
			} else {
				notification.error({
					message: "Sync Failed",
					description:
						response?.data?.message || "Failed to sync with Route4me",
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: "Sync Failed",
				description: "An error occurred while syncing with Route4me",
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setSyncing(false)
		}
	}

	const handleRoute4meClick = () => {
		setShowRoute4meModal(true)
		setRoute4meWarning(false)
	}

	const menu = (
		<Menu>
			<Menu.Item
				key="route4me-sync"
				icon={<Icon name="sync" />}
				onClick={handleRoute4meClick}
				disabled={isAllContracts}
			>
				<div>
					<div style={{ fontWeight: 500 }}>{Strings.orders.syncRoute4me}</div>
					<div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 2 }}>
						{isAllContracts
							? Strings.routes.contractRequiredForAction
							: Strings.orders.syncRoute4meTooltip}
					</div>
				</div>
			</Menu.Item>
		</Menu>
	)

	return (
		<div style={{ marginLeft: 8 }}>
			<Dropdown overlay={menu} trigger={["click"]}>
				<Button style={{ display: "flex", alignItems: "center" }}>
					<Icon name="preferences" />
				</Button>
			</Dropdown>

			{/* Route4me Sync Confirmation Modal */}
			<Modal
				title="Route4me Sync"
				visible={showRoute4meModal}
				onCancel={() => {
					setShowRoute4meModal(false)
					setRoute4meWarning(false)
				}}
				footer={[
					<Button
						key="cancel"
						onClick={() => {
							setShowRoute4meModal(false)
							setRoute4meWarning(false)
						}}
					>
						{Strings.generic.cancel}
						{route4meWarning && "!"}
					</Button>,
					<Button
						key="sync"
						type="primary"
						danger={route4meWarning}
						loading={syncing}
						onClick={() => handleRoute4meSync(route4meWarning)}
					>
						{Strings.generic.sync}
					</Button>,
				]}
			>
				<p style={{ marginBottom: 0 }}>
					{route4meWarning
						? Strings.orders.route4meSyncOnTransitWarnMessage
						: Strings.orders.route4meSyncMessage}
				</p>
			</Modal>
		</div>
	)
}

export default RouteOptionsButton
